import React, { createContext, useRef, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import Domain from '../../../domain/models/Domain';
import getDomains from '../../../domain/use_cases/domains/getDomains';
import getDomainById from '../../../domain/use_cases/domains/getDomainById';
import getScenariosByDomainId from '../../../domain/use_cases/scenarios/getScenariosByDomainId';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import Scenario from '../../../domain/models/Scenario';
import getDomainBySubscription from '../../../domain/use_cases/domains/getDomainBySubscription';

export const DOMAINS_FETCH_LIMIT = 10;

const DomainsContext = createContext({
  count: 0,
  hitsPerPage: DOMAINS_FETCH_LIMIT,
  items: [] as Domain[],
  selected: null as Domain | null,
  selectedScenarios: [] as Scenario[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  fetch: (bypassFetchLimit?: boolean, limit?: number) => new Promise<void>((resolve, reject) => null),
  fetchNext: () => new Promise<void>((resolve, reject) => null),
  fetchAndSelectById: (_: string) => new Promise<void>((resolve, reject) => null),
  fetchBySubscription: (_: string) => new Promise<Domain | null>((resolve, reject) => null),
  select: (_: Domain) => new Promise<void>((resolve, reject) => null),
  reset: () => {return;},
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
});

export const DomainsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(DOMAINS_FETCH_LIMIT);
  const [items, setItems] = useState([] as Domain[]);
  const [selected, setSelected] = useState(null as Domain | null);
  const [selectedScenarios, setSelectedScenarios] = useState([] as Scenario[]);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);

  // Selects mounted together (one per demo account row) wait for the same full load
  const fullLoad = useRef<Promise<void> | null>(null);

  const fetch = (bypassFetchLimit = false, limit?: number): Promise<void> => {
    if (bypassFetchLimit && fullLoad.current) return fullLoad.current;
    const load = (async () => {
      const effectiveLimit = limit ?? hitsPerPage;
      if (limit !== undefined) setHitsPerPage(limit);
      setStatus(ContextStatus.LOADING);
      try {
        const firestoreMaxLimit = 10000;
        const useCaseResponse = await getDomains(
          bypassFetchLimit ? firestoreMaxLimit : effectiveLimit
        );
        setItems(useCaseResponse.domains);
        setLastVisible(useCaseResponse.lastVisible);
        setCount(useCaseResponse.totalCollectionCount);
        setStatus(ContextStatus.SUCCESS);
      } catch (e) {
        setStatus(ContextStatus.ERROR);
      }
    })();
    if (bypassFetchLimit) {
      fullLoad.current = load.finally(() => {
        fullLoad.current = null;
      });
    }
    return load;
  };

  const fetchNext = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getDomains(
        hitsPerPage,
        lastVisible
      );
      setItems([...items, ...useCaseResponse.domains]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (domainId: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getDomainById(domainId);
      if (useCaseResponse) {
        setSelected(useCaseResponse);
        // We get the scenarios along with the domain
        const scenarioResponse = await getScenariosByDomainId(domainId);
        setSelectedScenarios(scenarioResponse.scenarios);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchBySubscription = async (subscriptionId: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getDomainBySubscription(subscriptionId);
      
      setStatus(ContextStatus.SUCCESS);
      return useCaseResponse;
    } catch (e) {
      setStatus(ContextStatus.ERROR);
      return null;
    }
  };

  const select = async (domain: Domain) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      setSelected(domain);
      // Again, we get the scenarios along with the domain
      const scenarioResponse = await getScenariosByDomainId(domain.id);
      setSelectedScenarios(scenarioResponse.scenarios);

      setSelectionStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(DOMAINS_FETCH_LIMIT);
    setItems([]);
    setSelected(null);
    setSelectedScenarios([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setSelectionStatus(ContextStatus.IDLE);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <DomainsContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        selected,
        selectedScenarios: selectedScenarios,
        lastVisible,
        fetch,
        fetchNext,
        fetchAndSelectById,
        fetchBySubscription,
        select,
        reset,
        status,
        selectionStatus,
      }}
    >
      {children}
    </DomainsContext.Provider>
  );
};

export default DomainsContext;
